import { ApolloError, WatchQueryFetchPolicy } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { RefreshControl } from 'react-native';

import {
  Document,
  DocumentSortOption,
  ListProjectDocumentsQuery,
  Project,
  User,
  useListProjectDocumentsQuery,
} from '@graphql/generated';
import { useGetDocumentsByClientIdsQuery } from '@hooks/useGetDocumentsByClientIdsQuery';
import { logApolloError } from '@utils/logNonFatalError';

const DEFAULT_PER_PAGE_COUNT = 15;

export const useListProjectDocumentsFromQuery = ({
  projectIds = undefined,
  ownerIds = undefined,
  sortBy = undefined,
  first = DEFAULT_PER_PAGE_COUNT,
  last = undefined,
  after = undefined,
  before = undefined,
  includeFiles = undefined,
  includeMedia = undefined,
  fetchPolicy = 'cache-and-network',
  skip = false,
  onCompleted = undefined,
  onFetchMoreCompleted = undefined,
}: {
  projectIds?: Project['id'][];
  ownerIds?: User['id'][];

  sortBy?: DocumentSortOption;
  limit?: number;
  first?: number;
  last?: number;
  after?: string;
  before?: string;
  includeFiles?: boolean;
  includeMedia?: boolean;
  fetchPolicy?: WatchQueryFetchPolicy;
  /** Hold the query until its variables are real (e.g. projectId resolved). */
  skip?: boolean;
  onCompleted?: (data?: ListProjectDocumentsQuery) => void;
  onFetchMoreCompleted?: () => void;
}) => {
  const [refreshing, setRefreshing] = useState(false);
  const [listProjectDocumentsData, setListProjectDocumentsData] = useState<
    ListProjectDocumentsQuery | undefined
  >();

  const { checkDocumentURL } = useGetDocumentsByClientIdsQuery();

  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  const { data, loading, error, refetch, fetchMore } =
    useListProjectDocumentsQuery({
      variables: {
        projectIds,
        ownerIds,
        sortBy,
        first,
        last,
        before,
        after,
        includeFiles,
        includeMedia,
      },
      fetchPolicy,
      skip,
    });

  useEffect(() => {
    if (!data) return;
    setListProjectDocumentsData(data);
    onCompletedRef.current?.(data);
    setRefreshing(false);
    if ((data.listProjectDocuments.edges?.length ?? 0) > 0) {
      checkDocumentURL(data.listProjectDocuments?.edges ?? []);
    }
  }, [data]);

  useEffect(() => {
    if (error) setRefreshing(false);
  }, [error]);

  // Apollo bails out of emitting a new `data`/`error` reference when a
  // refetch resolves to content deep-equal to what's already cached (e.g.
  // no new documents since the last check), so the effects above never
  // fire and refreshing is left stuck true. Resolving/rejecting this
  // promise is the only signal that's guaranteed regardless of Apollo's
  // cache diffing (TA-10476 QA follow-up).
  const onRefresh = () => {
    setRefreshing(true);
    refetch()
      .catch(() => {})
      .finally(() => setRefreshing(false));
  };
  const refreshControl = (
    <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
  );

  const { listProjectDocuments } = data ||
    listProjectDocumentsData || {
      listProjectDocuments: undefined,
    };
  const { edges = [], pageInfo } = listProjectDocuments || { edges: [] };
  const allDocuments =
    edges?.reduce((acc, curr) => {
      if (curr?.node !== undefined) {
        return [...acc, curr.node as Document];
      } else {
        return acc;
      }
    }, [] as Document[]) || [];
  // fetchMore's updateQuery appends every loaded page into this same cache
  // entry (there is no merge policy for listProjectDocuments). On a revisit,
  // cache-and-network would first render all of them and then collapse to
  // the refreshed first page, jumping the list under the user. `loading`
  // only flips for the initial/variables-change request (not fetchMore or
  // refetch), so while it's true show just the first page the refresh will
  // return.
  const documents = loading ? allDocuments.slice(0, first) : allDocuments;
  // The cached pageInfo describes the deepest page loaded last time, so after
  // a visit that reached the end it says hasNextPage: false while only the
  // first page is shown. Report a next page while a full first page is on
  // screen during the refresh, so callers that gate load-more on hasNextPage
  // still hand it to the deferral below; it re-checks the refreshed pageInfo.
  const exposedPageInfo =
    loading && pageInfo && allDocuments.length >= first
      ? { ...pageInfo, hasNextPage: true }
      : pageInfo;
  // `data` is undefined while new variables (sort/filter/project) load
  // uncached; `documents` then falls back to the previous variables' list.
  const isPreviousResult = !data && !!listProjectDocumentsData;

  // A load-more that arrives during the background refresh is deferred, not
  // dropped: the cached list is visible while `loading`, so the user can hit
  // the end of it, and onEndReached won't fire again if the refresh lands
  // deep-equal. Running fetchMore concurrently would also let the refresh
  // overwrite the appended page.
  const pendingFetchMoreRef = useRef(false);

  const fetchFromCursor = () => {
    if (loading) {
      pendingFetchMoreRef.current = true;
      return;
    }
    if (!pageInfo?.hasNextPage) {
      onFetchMoreCompleted?.();
      return;
    }
    const cursor = pageInfo?.endCursor;
    fetchMore({
      variables: {
        after: cursor,
        first,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const newEdges = fetchMoreResult.listProjectDocuments.edges;
        const pageInfo = fetchMoreResult.listProjectDocuments.pageInfo;

        if (newEdges?.length) {
          checkDocumentURL(newEdges);
        }
        onFetchMoreCompleted?.();

        return newEdges?.length
          ? {
              // Put the new comments at the end of the list and update `pageInfo`
              // so we have the new `endCursor` and `hasNextPage` values
              listProjectDocuments: {
                __typename: previousResult.listProjectDocuments.__typename,
                edges: [
                  ...previousResult.listProjectDocuments.edges,
                  ...newEdges,
                ],
                pageInfo,
              },
            }
          : previousResult;
      },
    }).catch((error) => {
      // A GraphQL error in the next page (errorPolicy 'none') rejects
      // fetchMore and updateQuery never runs — without this catch the
      // rejection is silent and callers waiting on onFetchMoreCompleted
      // (e.g. the media viewer's loading state, TA-9771) hang forever.
      if (error instanceof ApolloError) {
        logApolloError(error, 'listProjectDocuments fetchMore', [
          { after: cursor, first },
        ]);
      }
      onFetchMoreCompleted?.();
    });
  };

  // Runs with this render's fetchFromCursor, so it sees the refreshed
  // pageInfo/endCursor rather than the cached one.
  useEffect(() => {
    if (loading || !pendingFetchMoreRef.current) return;
    pendingFetchMoreRef.current = false;
    fetchFromCursor();
  }, [loading]);

  return {
    documents: documents,
    isPreviousResult,
    loading,
    error,
    pageInfo: exposedPageInfo,
    refetch,
    fetchFromCursor,
    refreshing,
    setRefreshing,
    refreshControl,
    onRefresh,
  };
};
